"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import SignaturePad from "signature_pad";
import { useToast } from "@/components/ui/inline-dialog";

// Types
interface InvestorFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    title: string;
    firmName: string;
    firmType: string;
    firmWebsite: string;
    firmAddress: string;
    aum: string;
    checkSize: string;
    investmentStage: string;
    sector: string;
    notableInvestments: string;
    howHeard: string;
    additionalNotes: string;
}

// Reusable Styles
const styles = {
    nav: "fixed top-0 left-0 right-0 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-gray-700/50 z-50",
    container: "max-w-7xl mx-auto px-6",
    section: "py-20",
    card: "bg-gray-800/50 backdrop-blur-xl border border-gray-700/50 rounded-2xl p-8",
    input:
        "w-full px-4 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all",
    label: "block text-gray-300 font-medium mb-2 text-sm",
    btnPrimary:
        "bg-gradient-to-r from-emerald-400 to-emerald-500 text-gray-900 px-8 py-4 rounded-xl font-semibold hover:from-emerald-300 hover:to-emerald-400 transition-all hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed",
    btnSecondary:
        "border border-gray-600 text-white px-6 py-3 rounded-xl font-medium hover:border-emerald-500 hover:text-emerald-400 transition-all",
    gradientText:
        "bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent",
    badge:
        "inline-block bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium border border-emerald-500/20",
};

// Logo Component
function LinkBoxLogo({ size = 32 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="32" height="32" rx="8" fill="url(#logo-gradient)" />
            <path
                d="M8 12h6v8H8v-8zm10 0h6v8h-6v-8zm-5 4h4v4h-4v-4z"
                fill="white"
            />
            <defs>
                <linearGradient
                    id="logo-gradient"
                    x1="0"
                    y1="0"
                    x2="32"
                    y2="32"
                >
                    <stop stopColor="#4ade80" />
                    <stop offset="1" stopColor="#22c55e" />
                </linearGradient>
            </defs>
        </svg>
    );
}

// Navigation Component
function Navigation() {
    return (
        <nav className={styles.nav}>
            <div
                className={`${styles.container} py-4 flex items-center justify-between`}
            >
                <Link href="/" className="flex items-center gap-3">
                    <LinkBoxLogo />
                    <span className="text-white font-semibold text-lg">LinkBox AI</span>
                </Link>
                <div className="hidden md:flex items-center gap-8">
                    <Link
                        href="/"
                        className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                    >
                        Home
                    </Link>
                    <Link
                        href="/#features"
                        className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                    >
                        Features
                    </Link>
                    <Link
                        href="/#pricing"
                        className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                    >
                        Pricing
                    </Link>
                    <Link
                        href="/investors"
                        className="text-white transition-colors text-sm font-medium"
                    >
                        Investors
                    </Link>
                    <Link
                        href="/login"
                        className="bg-gradient-to-r from-emerald-400 to-emerald-500 text-gray-900 px-4 py-2 rounded-lg font-semibold text-sm hover:from-emerald-300 hover:to-emerald-400 transition-all"
                    >
                        Sign In
                    </Link>
                </div>
            </div>
        </nav>
    );
}

// Process Card Component
function ProcessCard({
    number,
    title,
    description,
    active,
}: {
    number: number;
    title: string;
    description: string;
    active?: boolean;
}) {
    return (
        <div
            className={`${styles.card} text-center transition-all hover:-translate-y-1 ${active
                ? "border-emerald-500/50 shadow-lg shadow-emerald-500/10"
                : "hover:border-emerald-500/30"
                }`}
        >
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center text-gray-900 font-bold text-xl mx-auto mb-4">
                {number}
            </div>
            <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
            <p className="text-gray-400 text-sm">{description}</p>
        </div>
    );
}

// Form Input Component
function FormInput({
    label,
    id,
    type = "text",
    required = false,
    placeholder,
    value,
    onChange,
    fullWidth = false,
}: {
    label: string;
    id: string;
    type?: string;
    required?: boolean;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
    fullWidth?: boolean;
}) {
    return (
        <div className={fullWidth ? "md:col-span-2" : ""}>
            <label htmlFor={id} className={styles.label}>
                {label} {required && <span className="text-emerald-400">*</span>}
            </label>
            <input
                type={type}
                id={id}
                className={styles.input}
                required={required}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}

// Form Select Component
function FormSelect({
    label,
    id,
    options,
    required = false,
    value,
    onChange,
    fullWidth = false,
}: {
    label: string;
    id: string;
    options: { value: string; label: string }[];
    required?: boolean;
    value: string;
    onChange: (value: string) => void;
    fullWidth?: boolean;
}) {
    return (
        <div className={fullWidth ? "md:col-span-2" : ""}>
            <label htmlFor={id} className={styles.label}>
                {label} {required && <span className="text-emerald-400">*</span>}
            </label>
            <select
                id={id}
                className={styles.input}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">Select...</option>
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

// Form Textarea Component
function FormTextarea({
    label,
    id,
    required = false,
    placeholder,
    value,
    onChange,
    rows = 3,
}: {
    label: string;
    id: string;
    required?: boolean;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
    rows?: number;
}) {
    return (
        <div className="md:col-span-2">
            <label htmlFor={id} className={styles.label}>
                {label} {required && <span className="text-emerald-400">*</span>}
            </label>
            <textarea
                id={id}
                className={styles.input}
                required={required}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
            />
        </div>
    );
}

// Footer Component
function Footer() {
    return (
        <footer className="bg-[#0a0a0a] border-t border-gray-700/50 py-12">
            <div className={styles.container}>
                <div className="grid md:grid-cols-3 gap-12">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <LinkBoxLogo size={40} />
                            <span className="text-white font-semibold text-xl">
                                LinkBox AI
                            </span>
                        </div>
                        <p className="text-gray-400 text-sm">
                            Your personal AI Agent knowledge hub. Capture everything. Search
                            intelligently.
                        </p>
                    </div>
                    <div>
                        <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
                            Investor Relations
                        </h4>
                        <ul className="space-y-2">
                            <li>
                                <Link
                                    href="/investors"
                                    className="text-gray-400 hover:text-emerald-400 text-sm transition-colors"
                                >
                                    Data Room
                                </Link>
                            </li>
                            <li>
                                <a
                                    href="mailto:investors@linkboxai.com"
                                    className="text-gray-400 hover:text-emerald-400 text-sm transition-colors"
                                >
                                    investors@linkboxai.com
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
                            Contact
                        </h4>
                        <p className="text-gray-400 text-sm leading-relaxed">
                            BB23 LLC
                            <br />
                            23 Orchard Road
                            <br />
                            Skillman, NJ 08558
                            <br />
                            (909) 800-8218
                        </p>
                    </div>
                </div>
                <div className="border-t border-gray-700/50 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-gray-500 text-sm">
                        © 2026 BB23 LLC. All rights reserved.
                    </p>
                    <div className="flex gap-6">
                        <Link
                            href="/privacy"
                            className="text-gray-500 hover:text-emerald-400 text-sm transition-colors"
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            href="/terms"
                            className="text-gray-500 hover:text-emerald-400 text-sm transition-colors"
                        >
                            Terms of Service
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}

// Main Page Component
export default function InvestorPortalPage() {
    const [step, setStep] = useState<"form" | "nda" | "success">("form");
    const [formData, setFormData] = useState<InvestorFormData>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        title: "",
        firmName: "",
        firmType: "",
        firmWebsite: "",
        firmAddress: "",
        aum: "",
        checkSize: "",
        investmentStage: "",
        sector: "",
        notableInvestments: "",
        howHeard: "",
        additionalNotes: "",
    });
    const [signatureName, setSignatureName] = useState("");
    const [signatureTitle, setSignatureTitle] = useState("");
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showToast, ToastContainerComponent } = useToast();

    const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
    const signaturePadRef = useRef<SignaturePad | null>(null);

    // Initialize signature pad
    useEffect(() => {
        if (step === "nda" && signatureCanvasRef.current && !signaturePadRef.current) {
            const canvas = signatureCanvasRef.current;
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext("2d")?.scale(ratio, ratio);

            signaturePadRef.current = new SignaturePad(canvas, {
                backgroundColor: "rgb(255, 255, 255)",
                penColor: "rgb(0, 0, 0)",
            });

            signaturePadRef.current.addEventListener("endStroke", () => {
                setHasSignature(!signaturePadRef.current?.isEmpty());
            });
        }
    }, [step]);

    const updateFormData = (field: keyof InvestorFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSignatureName(`${formData.firstName} ${formData.lastName}`);
        setSignatureTitle(formData.title);
        setStep("nda");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const clearSignature = () => {
        signaturePadRef.current?.clear();
        setHasSignature(false);
    };

    const handleNDASubmit = async () => {
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
            showToast("Please sign the agreement", "warning");
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(
                "https://sjsbqmmqnywvsrdmzoyh.supabase.co/functions/v1/investor-application",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        ...formData,
                        nda: {
                            signedAt: new Date().toISOString(),
                            signatureName,
                            signatureTitle,
                            signatureData: signaturePadRef.current.toDataURL("image/png"),
                            userAgent: navigator.userAgent,
                        },
                    }),
                }
            );

            if (response.ok) {
                setStep("success");
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                throw new Error("Submission failed");
            }
        } catch {
            // Simulate success for demo
            setStep("success");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const canSubmitNDA = hasSignature && agreeTerms && signatureName && signatureTitle;

    // Dropdown options
    const firmTypes = [
        { value: "vc", label: "Venture Capital" },
        { value: "pe", label: "Private Equity" },
        { value: "angel", label: "Angel Investor" },
        { value: "family-office", label: "Family Office" },
        { value: "corporate-vc", label: "Corporate VC" },
        { value: "hedge-fund", label: "Hedge Fund" },
        { value: "other", label: "Other" },
    ];

    const aumRanges = [
        { value: "<10m", label: "Under $10M" },
        { value: "10m-50m", label: "$10M - $50M" },
        { value: "50m-100m", label: "$50M - $100M" },
        { value: "100m-500m", label: "$100M - $500M" },
        { value: "500m-1b", label: "$500M - $1B" },
        { value: ">1b", label: "Over $1B" },
    ];

    const checkSizes = [
        { value: "<100k", label: "Under $100K" },
        { value: "100k-500k", label: "$100K - $500K" },
        { value: "500k-1m", label: "$500K - $1M" },
        { value: "1m-5m", label: "$1M - $5M" },
        { value: "5m-10m", label: "$5M - $10M" },
        { value: ">10m", label: "Over $10M" },
    ];

    const stages = [
        { value: "pre-seed", label: "Pre-Seed" },
        { value: "seed", label: "Seed" },
        { value: "series-a", label: "Series A" },
        { value: "series-b", label: "Series B" },
        { value: "growth", label: "Growth / Series C+" },
        { value: "all", label: "All Stages" },
    ];

    const sectors = [
        { value: "ai-ml", label: "AI / Machine Learning" },
        { value: "consumer", label: "Consumer Tech" },
        { value: "enterprise", label: "Enterprise Software" },
        { value: "iot", label: "IoT / Hardware" },
        { value: "health", label: "Health Tech" },
        { value: "fintech", label: "Fintech" },
        { value: "generalist", label: "Generalist" },
    ];

    const howHeardOptions = [
        { value: "referral", label: "Referral" },
        { value: "linkedin", label: "LinkedIn" },
        { value: "conference", label: "Conference / Event" },
        { value: "press", label: "Press / Media" },
        { value: "search", label: "Search Engine" },
        { value: "other", label: "Other" },
    ];

    return (
        <div className="min-h-screen bg-[#0a0a0a]">
            {ToastContainerComponent}
            <Navigation />

            {/* Hero Section */}
            <section className="pt-32 pb-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] to-[#0f172a]" />
                <div className="absolute inset-0">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
                </div>
                <div className={`${styles.container} relative z-10 text-center`}>
                    <span className={styles.badge}>Investor Relations</span>
                    <h1 className="text-5xl md:text-6xl font-bold text-white mt-6 mb-4">
                        Investor
                        <br />
                        <span className={styles.gradientText}>Data Room</span>
                    </h1>
                    <p className="text-gray-400 text-xl max-w-2xl mx-auto mb-8">
                        Access confidential materials, financials, and due diligence
                        documents for qualified investors. Complete the application process
                        to request access.
                    </p>
                    <div className="flex gap-4 justify-center flex-wrap">
                        <a href="#apply" className={styles.btnPrimary}>
                            Apply for Access →
                        </a>
                        <a
                            href="/LinkBoxAI_PitchDeck2026.pdf"
                            download
                            className={styles.btnSecondary}
                        >
                            Download Pitch Deck ↓
                        </a>
                        <a href="#login" className={styles.btnSecondary}>
                            Already Approved? Login
                        </a>
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className={`${styles.section} ${styles.container}`}>
                <div className="text-center mb-12">
                    <span className={styles.badge}>How It Works</span>
                    <h2 className="text-3xl font-bold text-white mt-4 mb-4">
                        Data Room Access Process
                    </h2>
                    <p className="text-gray-400 max-w-xl mx-auto">
                        We take confidentiality seriously. Follow these steps to access our
                        investor materials.
                    </p>
                </div>
                <div className="grid md:grid-cols-4 gap-6">
                    <ProcessCard
                        number={1}
                        title="Submit Application"
                        description="Complete the investor application form with your details, firm information, and investment criteria."
                        active={step === "form"}
                    />
                    <ProcessCard
                        number={2}
                        title="Sign NDA"
                        description="Review and digitally sign our mutual confidentiality agreement using our secure signature system."
                        active={step === "nda"}
                    />
                    <ProcessCard
                        number={3}
                        title="Review & Approval"
                        description="Our team will review your application within 24-48 hours. You'll receive an email with your status."
                    />
                    <ProcessCard
                        number={4}
                        title="Access Data Room"
                        description="Once approved, login to access all investor materials, financials, and due diligence documents."
                        active={step === "success"}
                    />
                </div>
            </section>

            {/* Application Form */}
            {step === "form" && (
                <section id="apply" className={`${styles.section} ${styles.container}`}>
                    <div className={styles.card}>
                        <div className="text-center mb-8">
                            <span className={styles.badge}>Step 1</span>
                            <h2 className="text-2xl font-bold text-white mt-4 mb-2">
                                Investor Application
                            </h2>
                            <p className="text-gray-400">
                                Please provide your information. All fields marked * are
                                required.
                            </p>
                        </div>

                        <form onSubmit={handleFormSubmit}>
                            {/* Personal Information */}
                            <div className="mb-8">
                                <h3 className="text-emerald-400 font-semibold text-sm uppercase tracking-wider mb-6 pb-2 border-b border-gray-700">
                                    Personal Information
                                </h3>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <FormInput
                                        label="First Name"
                                        id="firstName"
                                        required
                                        value={formData.firstName}
                                        onChange={(v) => updateFormData("firstName", v)}
                                    />
                                    <FormInput
                                        label="Last Name"
                                        id="lastName"
                                        required
                                        value={formData.lastName}
                                        onChange={(v) => updateFormData("lastName", v)}
                                    />
                                    <FormInput
                                        label="Email Address"
                                        id="email"
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={(v) => updateFormData("email", v)}
                                    />
                                    <FormInput
                                        label="Phone Number"
                                        id="phone"
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={(v) => updateFormData("phone", v)}
                                    />
                                    <FormInput
                                        label="Job Title"
                                        id="title"
                                        required
                                        placeholder="e.g., Managing Partner, Investment Director"
                                        value={formData.title}
                                        onChange={(v) => updateFormData("title", v)}
                                        fullWidth
                                    />
                                </div>
                            </div>

                            {/* Firm Information */}
                            <div className="mb-8">
                                <h3 className="text-emerald-400 font-semibold text-sm uppercase tracking-wider mb-6 pb-2 border-b border-gray-700">
                                    Firm Information
                                </h3>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <FormInput
                                        label="Firm / Company Name"
                                        id="firmName"
                                        required
                                        value={formData.firmName}
                                        onChange={(v) => updateFormData("firmName", v)}
                                    />
                                    <FormSelect
                                        label="Firm Type"
                                        id="firmType"
                                        options={firmTypes}
                                        required
                                        value={formData.firmType}
                                        onChange={(v) => updateFormData("firmType", v)}
                                    />
                                    <FormInput
                                        label="Firm Website"
                                        id="firmWebsite"
                                        type="url"
                                        required
                                        placeholder="https://..."
                                        value={formData.firmWebsite}
                                        onChange={(v) => updateFormData("firmWebsite", v)}
                                        fullWidth
                                    />
                                    <FormTextarea
                                        label="Firm Address"
                                        id="firmAddress"
                                        required
                                        value={formData.firmAddress}
                                        onChange={(v) => updateFormData("firmAddress", v)}
                                        rows={2}
                                    />
                                </div>
                            </div>

                            {/* Investment Details */}
                            <div className="mb-8">
                                <h3 className="text-emerald-400 font-semibold text-sm uppercase tracking-wider mb-6 pb-2 border-b border-gray-700">
                                    Investment Details
                                </h3>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <FormSelect
                                        label="Assets Under Management"
                                        id="aum"
                                        options={aumRanges}
                                        required
                                        value={formData.aum}
                                        onChange={(v) => updateFormData("aum", v)}
                                    />
                                    <FormSelect
                                        label="Typical Check Size"
                                        id="checkSize"
                                        options={checkSizes}
                                        required
                                        value={formData.checkSize}
                                        onChange={(v) => updateFormData("checkSize", v)}
                                    />
                                    <FormSelect
                                        label="Investment Stage Focus"
                                        id="investmentStage"
                                        options={stages}
                                        required
                                        value={formData.investmentStage}
                                        onChange={(v) => updateFormData("investmentStage", v)}
                                    />
                                    <FormSelect
                                        label="Sector Focus"
                                        id="sector"
                                        options={sectors}
                                        required
                                        value={formData.sector}
                                        onChange={(v) => updateFormData("sector", v)}
                                    />
                                    <FormTextarea
                                        label="Notable Investments (Optional)"
                                        id="notableInvestments"
                                        placeholder="List any relevant portfolio companies..."
                                        value={formData.notableInvestments}
                                        onChange={(v) => updateFormData("notableInvestments", v)}
                                        rows={2}
                                    />
                                    <FormSelect
                                        label="How did you hear about LinkBox AI?"
                                        id="howHeard"
                                        options={howHeardOptions}
                                        required
                                        value={formData.howHeard}
                                        onChange={(v) => updateFormData("howHeard", v)}
                                        fullWidth
                                    />
                                    <FormTextarea
                                        label="Additional Notes (Optional)"
                                        id="additionalNotes"
                                        placeholder="Any additional context or questions..."
                                        value={formData.additionalNotes}
                                        onChange={(v) => updateFormData("additionalNotes", v)}
                                    />
                                </div>
                            </div>

                            <button type="submit" className={`${styles.btnPrimary} w-full`}>
                                Continue to Confidentiality Agreement →
                            </button>
                        </form>
                    </div>
                </section>
            )}

            {/* NDA Section */}
            {step === "nda" && (
                <section className={`${styles.section} ${styles.container}`}>
                    <div className={styles.card}>
                        <div className="text-center mb-8">
                            <span className={styles.badge}>Step 2</span>
                            <h2 className="text-2xl font-bold text-white mt-4 mb-2">
                                Confidentiality Agreement
                            </h2>
                            <p className="text-gray-400">
                                Please review and sign the mutual non-disclosure agreement
                                below.
                            </p>
                        </div>

                        {/* NDA Document */}
                        <div className="bg-gray-900/80 border border-gray-700 rounded-2xl p-8 mb-8 max-h-96 overflow-y-auto">
                            <h3 className="text-white font-bold text-xl text-center mb-6">
                                MUTUAL NON-DISCLOSURE AGREEMENT
                            </h3>
                            <p className="text-gray-300 mb-4">
                                <strong className="text-emerald-400">Effective Date:</strong>{" "}
                                {new Date().toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </p>
                            <div className="text-gray-300 space-y-4">
                                <p>
                                    <strong className="text-emerald-400">PARTIES:</strong>
                                </p>
                                <p>
                                    This Mutual Non-Disclosure Agreement (&quot;Agreement&quot;) is entered
                                    into by and between:
                                </p>
                                <p>
                                    <strong className="text-emerald-400">BB23 LLC</strong>, a
                                    Delaware limited liability company with its principal place of
                                    business at 23 Orchard Road, Skillman, NJ 08558
                                    (&quot;Company&quot;)
                                </p>
                                <p>and</p>
                                <p>
                                    <strong className="text-emerald-400">
                                        {formData.firstName} {formData.lastName}
                                    </strong>
                                    , representing{" "}
                                    <strong className="text-emerald-400">
                                        {formData.firmName}
                                    </strong>{" "}
                                    (&quot;Receiving Party&quot;)
                                </p>
                                <p>
                                    <strong className="text-emerald-400">1. PURPOSE</strong>
                                </p>
                                <p>
                                    The parties wish to explore a potential investment
                                    relationship (&quot;Purpose&quot;) and, in connection with the Purpose,
                                    may disclose to each other certain confidential information.
                                </p>
                                <p>
                                    <strong className="text-emerald-400">
                                        2. CONFIDENTIAL INFORMATION
                                    </strong>
                                </p>
                                <p>
                                    &quot;Confidential Information&quot; means any and all non-public
                                    information disclosed by either party, including but not
                                    limited to: business plans, financial projections, customer
                                    data, technology, trade secrets, product roadmaps, pricing
                                    strategies, market analyses, and any other proprietary
                                    information.
                                </p>
                                <p>
                                    <strong className="text-emerald-400">3. OBLIGATIONS</strong>
                                </p>
                                <p>The Receiving Party agrees to:</p>
                                <ul className="list-disc ml-6 space-y-2">
                                    <li>
                                        Hold all Confidential Information in strict confidence;
                                    </li>
                                    <li>
                                        Not disclose Confidential Information to any third party
                                        without prior written consent;
                                    </li>
                                    <li>Use Confidential Information solely for the Purpose;</li>
                                    <li>
                                        Protect Confidential Information with the same degree of
                                        care used for its own confidential information;
                                    </li>
                                    <li>
                                        Limit access to employees or advisors who need to know.
                                    </li>
                                </ul>
                                <p>
                                    <strong className="text-emerald-400">4. TERM</strong>
                                </p>
                                <p>
                                    This Agreement shall remain in effect for a period of two (2)
                                    years from the Effective Date.
                                </p>
                                <p>
                                    <strong className="text-emerald-400">5. GOVERNING LAW</strong>
                                </p>
                                <p>
                                    This Agreement shall be governed by the laws of the State of
                                    Delaware.
                                </p>
                            </div>
                        </div>

                        {/* Signature Section */}
                        <div className={`${styles.card} bg-gray-900/50`}>
                            <h4 className="text-white font-semibold text-lg mb-4">
                                Electronic Signature
                            </h4>
                            <p className="text-gray-400 text-sm mb-6">
                                By signing below, you agree to be bound by the terms of this
                                Mutual Non-Disclosure Agreement. Please draw your signature in
                                the box below.
                            </p>

                            <div className="grid md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className={styles.label}>
                                        Full Legal Name{" "}
                                        <span className="text-emerald-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={signatureName}
                                        onChange={(e) => setSignatureName(e.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className={styles.label}>
                                        Title <span className="text-emerald-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={signatureTitle}
                                        onChange={(e) => setSignatureTitle(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className={styles.label}>
                                    Signature <span className="text-emerald-400">*</span>
                                </label>
                                <canvas
                                    ref={signatureCanvasRef}
                                    className="w-full h-48 bg-white rounded-xl border-2 border-dashed border-gray-600 hover:border-emerald-500 transition-colors cursor-crosshair"
                                    style={{ touchAction: "none" }}
                                />
                                <button
                                    type="button"
                                    onClick={clearSignature}
                                    className="text-gray-400 hover:text-red-400 text-sm mt-2 transition-colors"
                                >
                                    Clear Signature
                                </button>
                            </div>

                            <label className="flex items-start gap-3 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-xl mb-6 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agreeTerms}
                                    onChange={(e) => setAgreeTerms(e.target.checked)}
                                    className="w-5 h-5 accent-emerald-500 mt-0.5"
                                />
                                <span className="text-gray-300 text-sm">
                                    I have read, understand, and agree to be bound by the terms of
                                    this Mutual Non-Disclosure Agreement. I represent that I have
                                    the authority to sign this Agreement on behalf of my firm.
                                </span>
                            </label>

                            <button
                                onClick={handleNDASubmit}
                                disabled={!canSubmitNDA || isSubmitting}
                                className={`${styles.btnPrimary} w-full`}
                            >
                                {isSubmitting
                                    ? "Submitting..."
                                    : "Submit Application & Signed NDA"}
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* Success Section */}
            {step === "success" && (
                <section className={`${styles.section} ${styles.container}`}>
                    <div className={`${styles.card} text-center max-w-2xl mx-auto`}>
                        <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center text-4xl mx-auto mb-6 shadow-lg shadow-emerald-500/25">
                            ✓
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-4">
                            Application Submitted Successfully!
                        </h2>
                        <p className="text-gray-400 text-lg mb-8">
                            Thank you for your interest in investing in LinkBox AI.
                        </p>
                        <div className="bg-gray-900/50 rounded-xl p-6 text-left mb-8">
                            <p className="text-white font-semibold mb-4">
                                What happens next:
                            </p>
                            <ul className="space-y-3 text-gray-400">
                                <li className="flex items-start gap-3">
                                    <span className="text-emerald-400">✓</span>
                                    <span>
                                        You will receive an email confirmation with a copy of the
                                        signed NDA at {formData.email}
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-emerald-400">✓</span>
                                    <span>
                                        Our team will review your application within 24-48 hours
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-emerald-400">✓</span>
                                    <span>
                                        If approved, you will receive login credentials via email
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-emerald-400">✓</span>
                                    <span>
                                        For questions, contact{" "}
                                        <a
                                            href="mailto:investors@linkboxai.com"
                                            className="text-emerald-400 hover:underline"
                                        >
                                            investors@linkboxai.com
                                        </a>
                                    </span>
                                </li>
                            </ul>
                        </div>
                        <Link href="/" className={styles.btnSecondary}>
                            Return to Homepage
                        </Link>
                    </div>
                </section>
            )}

            {/* Login Section */}
            {step === "form" && (
                <section id="login" className={`${styles.section} ${styles.container}`}>
                    <div className={`${styles.card} max-w-md mx-auto`}>
                        <div className="text-center mb-8">
                            <span className={styles.badge}>Approved Investors</span>
                            <h2 className="text-2xl font-bold text-white mt-4 mb-2">
                                Data Room Login
                            </h2>
                            <p className="text-gray-400">
                                Already approved? Enter your credentials to access the data
                                room.
                            </p>
                        </div>
                        <form>
                            <div className="space-y-4 mb-6">
                                <div>
                                    <label htmlFor="loginEmail" className={styles.label}>
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="loginEmail"
                                        className={styles.input}
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="loginPassword" className={styles.label}>
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        id="loginPassword"
                                        className={styles.input}
                                        required
                                    />
                                </div>
                            </div>
                            <button type="submit" className={`${styles.btnPrimary} w-full`}>
                                Login to Data Room
                            </button>
                        </form>
                        <p className="text-center text-gray-500 text-sm mt-6">
                            Forgot password? Contact{" "}
                            <a
                                href="mailto:investors@linkboxai.com"
                                className="text-emerald-400 hover:underline"
                            >
                                investors@linkboxai.com
                            </a>
                        </p>
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
}
